import { countOf, ownValue } from "./shared/safe";

// Pure logic for the Fleet view: reading untrusted client rows, filtering, and attention-first ordering.

export const FLEET_STATUS_FILTERS = ["all", "needs_attention", "active", "onboarding", "paused", "archived"] as const;
export type FleetStatusFilter = (typeof FLEET_STATUS_FILTERS)[number];

export const FLEET_STATUS_LABELS: Record<FleetStatusFilter, string> = {
  all: "All statuses",
  needs_attention: "Needs attention",
  active: "Active",
  onboarding: "Onboarding",
  paused: "Paused",
  archived: "Archived",
};

const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "neutral"> = { active: "ok", onboarding: "neutral", paused: "warn", archived: "bad" };
export const fleetStatusTone = (status: string) => ownValue(STATUS_TONE, status) ?? "neutral";

export function parseFleetFilter(raw: unknown): FleetStatusFilter {
  return typeof raw === "string" && (FLEET_STATUS_FILTERS as readonly string[]).includes(raw) ? (raw as FleetStatusFilter) : "all";
}

export interface FleetClient {
  /** Null when the row has no usable id: it is listed but cannot be opened. */
  id: string | null;
  name: string;
  slug: string;
  city: string;
  state: string;
  status: string;
  mode: string;
  reconnectNeeded: number;
  reviewWaiting: number;
  sitesLive: number;
  sitesTotal: number;
  /** Lower-cased text the search box matches against. Kept in memory, not in a DOM attribute. */
  haystack: string;
}

const text = (v: unknown): string => (typeof v === "string" ? v : "");

export function normalizeClient(raw: unknown): FleetClient | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const name = text(r.business_name).trim() || "(unnamed client)";
  const slug = text(r.slug);
  const city = text(r.city);
  const state = text(r.state);
  return {
    id: typeof r.id === "string" && r.id !== "" ? r.id : null,
    name,
    slug,
    city,
    state,
    status: text(r.status) || "unknown",
    mode: text(r.mode),
    reconnectNeeded: countOf(r.reconnect_needed),
    reviewWaiting: countOf(r.review_waiting),
    sitesLive: countOf(r.sites_live),
    sitesTotal: countOf(r.sites_total),
    haystack: `${name} ${slug} ${city} ${state}`.toLowerCase(),
  };
}

export function parseClients(body: unknown): { clients: FleetClient[]; dropped: number } | null {
  if (typeof body !== "object" || body === null || !Array.isArray((body as { clients?: unknown }).clients)) return null;
  const clients: FleetClient[] = [];
  let dropped = 0;
  for (const raw of (body as { clients: unknown[] }).clients) {
    const c = normalizeClient(raw);
    if (c) clients.push(c);
    else dropped += 1;
  }
  return { clients, dropped };
}

export const needsAttention = (c: FleetClient): boolean => c.reconnectNeeded > 0 || c.reviewWaiting > 0;

/** Attention-first, stable: rows that need a decision come first; ties keep the API's order. */
export function sortByAttention(clients: readonly FleetClient[]): FleetClient[] {
  return clients
    .map((c, i) => ({ c, i }))
    .sort((a, b) => Number(needsAttention(b.c)) - Number(needsAttention(a.c)) || a.i - b.i)
    .map((x) => x.c);
}

export function filterClients(clients: readonly FleetClient[], query: string, status: FleetStatusFilter): FleetClient[] {
  const q = query.trim().toLowerCase();
  return clients.filter((c) => {
    if (status === "needs_attention" ? !needsAttention(c) : status !== "all" && c.status !== status) return false;
    return !q || c.haystack.includes(q);
  });
}

// ---- server-side search and paging (GET /v1/clients?q=&status=&limit=&offset=; the response then carries `page`) ----------------

export interface FleetPage {
  total: number;
  limit: number;
  offset: number;
  returned: number;
  hasMore: boolean;
}

/** `page` of a parameterised list response. Null when absent (an older server, or no parameter was honoured) or unreadable. */
export function parsePage(body: unknown): FleetPage | null {
  if (typeof body !== "object" || body === null) return null;
  const p = (body as { page?: unknown }).page;
  if (typeof p !== "object" || p === null || Array.isArray(p)) return null;
  const r = p as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null);
  const total = n(r.total);
  const limit = n(r.limit);
  const offset = n(r.offset);
  const returned = n(r.returned);
  if (total === null || limit === null || offset === null || returned === null || limit < 1 || typeof r.hasMore !== "boolean") return null;
  return { total, limit, offset, returned, hasMore: r.hasMore };
}

export const FLEET_PAGE_SIZE = 25;
/** The API's largest page; used to read the whole list for the "Needs attention" view, which the server cannot filter. */
export const FLEET_MAX_LIMIT = 200;

/** Query for the server: only what the server understands. "needs_attention" is not a server status (it is derived from counters). */
export function fleetQuery(search: string, status: FleetStatusFilter, offset: number, limit = FLEET_PAGE_SIZE): string {
  const q = new URLSearchParams();
  const term = search.trim();
  if (term) q.set("q", term.slice(0, 200));
  if (status !== "all" && status !== "needs_attention") q.set("status", status);
  q.set("limit", String(limit));
  q.set("offset", String(offset));
  return `?${q.toString()}`;
}

/** "26-50 of 64", or "0 of 0". */
export function rangeLabel(offset: number, returned: number, total: number): string {
  return returned === 0 ? `0 of ${total}` : `${offset + 1}-${offset + returned} of ${total}`;
}
